/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Utilities for UploadAttachment button
 */
import axios from 'axios';
import _ from 'lodash';
import type { ReactNode } from 'react';
import type { UploadProps } from 'antd';

const IN_FLIGHT_STATUSES: UploadManager.IStatus[] = ['queued', 'uploading', 'registering'];
const DEFAULT_CONTENT_TYPE = 'application/octet-stream';

interface NormalizedExtensions {
	set: Set<string>;
	accept?: string;
	label: string;
}

interface AttachmentValidationResult<TFile extends Pick<File, 'name' | 'size'>> {
	validFiles: TFile[];
	errorMessages: string[];
}

type UploadingLabel = ReactNode | ((count: number) => ReactNode);
type BlobUploadStatus = 'queued' | 'uploading' | 'failed';
type BlobUploadItem = {
	id: string;
	fileName: string;
	size: number;
	progress: number;
	status: BlobUploadStatus;
	error?: string;
};

const getAcceptedFileTypes = (allowedFileTypes?: string[], accept?: UploadProps['accept']): string[] =>
	_.flatMap(_.compact([allowedFileTypes, accept]), (fileTypes) => {
		if (_.isArray(fileTypes)) {
			return fileTypes;
		}
		if (_.isString(fileTypes)) {
			return _.map(fileTypes?.split(','), _.trim);
		}
		return [];
	});

const isBlobUploadInFlight = (upload: BlobUploadItem): boolean => upload.status === 'queued' || upload.status === 'uploading';

const createBlobUploadItem = (file: File): BlobUploadItem => ({
	id: _.uniqueId('blob-upload-'),
	fileName: file.name,
	size: file.size,
	progress: 0,
	status: 'queued',
});

const createCloudflareUploadFile = (file: File, data: PresignedUrlResponse): PatientPayment.ICloudflareUploadFile => ({
	blobId: data.blobId,
	name: file.name,
	type: file.type,
	uid: data.blobId || data.s3Path,
	status: 'done',
	url: data.s3Path,
});

const getUploadedFileKey = (file: Pick<PatientPayment.ICloudflareUploadFile, 'url' | 'uid'>): string => file.url || file.uid;

const normalizeAcceptedExtensions = (acceptedExtensions: string[]): NormalizedExtensions => {
	const extensions = _.uniq(_.compact(_.map(acceptedExtensions, (ext) => _.trimStart(ext, '.').toLowerCase())));

	return {
		set: new Set(extensions),
		accept: _.isEmpty(extensions) ? undefined : _.map(extensions, (ext) => `.${ext}`).join(', '),
		label: _.isEmpty(extensions) ? '' : _.map(extensions, (ext) => `.${_.toUpper(ext)}`).join(', '),
	};
};

const countEntityInFlightUploads = (uploads: UploadManager.ITask[], entityId: string, entityType: EntityTypeEnum): number => {
	return _.filter(
		uploads,
		(upload) => upload.entityId === entityId && upload.entityType === entityType && _.includes(IN_FLIGHT_STATUSES, upload.status),
	).length;
};

const resolveUploadButtonLabel = (inFlightCount: number, idleLabel: ReactNode, uploadingLabel?: UploadingLabel): ReactNode => {
	if (inFlightCount === 0) {
		return idleLabel;
	}
	if (_.isFunction(uploadingLabel)) {
		return uploadingLabel(inFlightCount);
	}
	return uploadingLabel ?? `Uploading (${inFlightCount})…`;
};

const validateAttachmentFile = (
	file: Pick<File, 'name' | 'size'>,
	extensions: NormalizedExtensions,
	fileSizeLimitMB: number,
): string | null => {
	const ext = _.toLower(_.last(_.split(file.name, '.')));
	if (extensions.set.size > 0 && (!ext || !extensions.set.has(ext))) {
		return extensions.label ? `Only ${extensions.label} files are supported` : 'Unsupported file type';
	}

	if (file.size / (1024 * 1024) > fileSizeLimitMB) {
		return `File must be ${fileSizeLimitMB}MB or smaller`;
	}

	return null;
};

const getUploadAcceptConfig = (
	allowedFileTypes?: string[],
	accept?: UploadProps['accept'],
): { acceptedExtensions: NormalizedExtensions; accept?: string } => {
	const acceptedExtensions = normalizeAcceptedExtensions(getAcceptedFileTypes(allowedFileTypes, accept));

	return {
		acceptedExtensions,
		accept: acceptedExtensions.accept || (_.isString(accept) ? accept : undefined),
	};
};

const getFileSizeLimitMB = (maxFileSize?: number | null): number =>
	_.isNumber(maxFileSize) ? maxFileSize / (1024 * 1024) : Number.POSITIVE_INFINITY;

const formatUploadFileSize = (bytes: number): string => {
	if (!_.isNumber(bytes)) {
		return '';
	}

	const units = ['B', 'KB', 'MB', 'GB', 'TB'];
	let size = bytes;
	let unitIndex = 0;

	while (size >= 1024 && unitIndex < units.length - 1) {
		size /= 1024;
		unitIndex += 1;
	}

	return `${_.round(size, 2)} ${units[unitIndex]}`;
};

const getAttachmentValidationMessage = (errorMessage: string, maxFileSize?: number | null): string => {
	return _.isNumber(maxFileSize) && errorMessage?.includes('MB or smaller')
		? `File size must be less than ${formatUploadFileSize(maxFileSize)}.`
		: errorMessage;
};

const partitionAttachmentFiles = <TFile extends Pick<File, 'name' | 'size'>>(
	files: TFile[],
	extensions: NormalizedExtensions,
	fileSizeLimitMB: number,
): AttachmentValidationResult<TFile> => {
	return files.reduce<AttachmentValidationResult<TFile>>(
		(result, file) => {
			const errorMessage = validateAttachmentFile(file, extensions, fileSizeLimitMB);
			if (errorMessage) {
				if (!result.errorMessages.includes(errorMessage)) {
					result.errorMessages.push(errorMessage);
				}
				return result;
			}

			result.validFiles.push(file);
			return result;
		},
		{ validFiles: [], errorMessages: [] },
	);
};

const getUploadErrorMessage = (error: unknown, fileName: string): string => {
	if (typeof error === 'string' && error?.trim().length > 0) {
		return error;
	}

	if (error instanceof Error && error.message.trim().length > 0) {
		return error.message;
	}

	return `Failed to upload ${fileName}`;
};

const uploadFileToPresignedUrl = async ({
	file,
	presignedUrl,
	onProgress,
}: {
	file: File;
	presignedUrl: string;
	onProgress?: (progress: number) => void;
}): Promise<void> => {
	await axios.put(presignedUrl, file, {
		headers: { 'Content-Type': file.type || DEFAULT_CONTENT_TYPE },
		onUploadProgress: (event) => {
			const total = event.total ?? file.size;
			const progress = total ? Math.round((event.loaded / total) * 100) : 0;
			onProgress?.(progress);
		},
	});
};

export {
	BlobUploadItem,
	getAcceptedFileTypes,
	getAttachmentValidationMessage,
	getFileSizeLimitMB,
	normalizeAcceptedExtensions,
	getUploadAcceptConfig,
	countEntityInFlightUploads,
	createBlobUploadItem,
	createCloudflareUploadFile,
	formatUploadFileSize,
	DEFAULT_CONTENT_TYPE,
	getUploadedFileKey,
	isBlobUploadInFlight,
	partitionAttachmentFiles,
	resolveUploadButtonLabel,
	getUploadErrorMessage,
	uploadFileToPresignedUrl,
	NormalizedExtensions,
};
